import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Modal } from 'react-bootstrap';
import { Eye } from 'react-bootstrap-icons';
import { getStoreSalaryStatementPreview } from '../../../services/storeSalaryStatementApi';
import '../../../assets/styles/salaryStatement.css';

const empty = { page: 1, limit: 50, total: 0, totalPages: 0, hasNext: false, hasPrev: false };
const displayValue = (value) => (value === null || value === undefined || value === '' ? '-' : value);

export default function StoreSalaryStatementPreview() {
  const { id } = useParams();
  const [data, setData] = useState({ headers: [], rows: [] });
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [limit] = useState(50);
  const [pagination, setPagination] = useState(empty);
  const [selectedRow, setSelectedRow] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const loadingRef = useRef(false);

  const headers = useMemo(() => data.headers || [], [data.headers]);
  const rows = data.rows || [];
  const tableColSpan = Math.max(headers.length + 1, 1);

  const serialForIndex = useCallback((rowIndex) => (
    (pagination.page - 1) * pagination.limit + rowIndex + 1
  ), [pagination.limit, pagination.page]);

  const load = useCallback(async ({ nextPage = 1, nextSearch = '' } = {}) => {
    if (loadingRef.current) return;

    loadingRef.current = true;
    setLoading(true);
    setErr('');
    setSelectedRow(null);
    setDetailRow(null);

    try {
      const response = await getStoreSalaryStatementPreview(id, { page: nextPage, limit, search: nextSearch });
      setData(response.data || { headers: [], rows: [] });
      setPagination(response.data?.pagination || { ...empty, page: nextPage, limit });
    } catch (e) {
      setErr(e.message);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [id, limit]);

  useEffect(() => {
    load({ nextPage: 1, nextSearch: '' });
  }, [load]);

  const apply = () => {
    const value = search.trim();
    setAppliedSearch(value);
    load({ nextPage: 1, nextSearch: value });
  };

  const clear = () => {
    setSearch('');
    setAppliedSearch('');
    load({ nextPage: 1, nextSearch: '' });
  };

  const go = (page) => {
    if (loading || page < 1 || page > pagination.totalPages) return;
    load({ nextPage: page, nextSearch: appliedSearch });
  };

  const rowKey = (row) => row.id || row.rowIndex;

  const handleRowSelect = (row) => {
    setSelectedRow(rowKey(row));
  };

  const openDetails = (event, row, rowIndex) => {
    event.stopPropagation();
    handleRowSelect(row);
    setDetailRow({ row, serialNumber: serialForIndex(rowIndex) });
  };

  const detailItems = useMemo(() => {
    if (!detailRow?.row) return [];
    return headers.map((header, index) => ({
      key: `${header}-${index}`,
      label: header,
      value: displayValue(detailRow.row.cells?.[header]),
    }));
  }, [detailRow, headers]);

  return (
    <div className="salary-statement-preview-page store-salary-statement-page">
      <h2>Salary Statement Preview</h2>
      {err && <div className="alert alert-warning">{err}</div>}
      {loading && !rows.length ? (
        <div className="store-card p-4 text-center"><span className="spinner-border spinner-border-sm me-2" />Loading...</div>
      ) : (
        <>
          <div className="card salary-statement-search-card p-3 mb-3">
            <div className="row g-2 align-items-center">
              <div className="col-md-6">
                <input
                  className="form-control"
                  placeholder="Search salary rows"
                  value={search}
                  disabled={loading}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') apply(); }}
                />
              </div>
              <div className="col-md-auto"><button type="button" className="btn btn-primary" disabled={loading} onClick={apply}>Search</button></div>
              <div className="col-md-auto"><button type="button" className="btn btn-outline-secondary" disabled={loading || (!search && !appliedSearch)} onClick={clear}>Clear</button></div>
              <div className="col-md-auto text-muted">Page {pagination.page} of {pagination.totalPages || 0} ({pagination.total} rows)</div>
            </div>
          </div>
          <div className="store-card salary-statement-table-card">
            <div className="salary-statement-table-wrapper">
              <table className="table table-bordered table-sm salary-statement-table">
                <thead>
                  <tr>
                    <th className="salary-statement-serial-cell">S.No</th>
                    {headers.map((header, index) => <th key={`${header}-${index}`}>{header}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={tableColSpan} className="text-center py-4"><span className="spinner-border spinner-border-sm me-2" />Loading...</td></tr>
                  ) : rows.length ? rows.map((row, rowIndex) => {
                    const key = rowKey(row);
                    const serialNumber = serialForIndex(rowIndex);
                    return (
                      <tr key={key} className={selectedRow === key ? 'salary-statement-row-selected' : ''} onClick={() => handleRowSelect(row)}>
                        <td className="salary-statement-serial-cell">
                          <span>{serialNumber}</span>
                          <button
                            type="button"
                            className="salary-statement-eye-button"
                            aria-label={`View salary row ${serialNumber} details`}
                            title="View details"
                            onClick={(event) => openDetails(event, row, rowIndex)}
                          >
                            <Eye aria-hidden="true" />
                          </button>
                        </td>
                        {headers.map((header, index) => (
                          <td className="salary-statement-cell" key={`${header}-${index}`} title={row.cells?.[header] ?? ''}>{displayValue(row.cells?.[header])}</td>
                        ))}
                      </tr>
                    );
                  }) : (
                    <tr><td colSpan={tableColSpan} className="text-center py-4">No salary rows found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="d-flex justify-content-between align-items-center p-3">
              <span>Page {pagination.page} of {pagination.totalPages || 0}</span>
              <div>
                <button type="button" className="btn btn-outline-secondary me-2" disabled={loading || !pagination.hasPrev} onClick={() => go(pagination.page - 1)}>Previous</button>
                <button type="button" className="btn btn-outline-secondary" disabled={loading || !pagination.hasNext} onClick={() => go(pagination.page + 1)}>Next</button>
              </div>
            </div>
          </div>
        </>
      )}

      <Modal show={!!detailRow} onHide={() => setDetailRow(null)} size="xl" centered scrollable dialogClassName="salary-row-detail-modal">
        <Modal.Header closeButton>
          <Modal.Title>Employee Salary Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="fw-bold mb-3">S.No: {detailRow?.serialNumber}</p>
          <div className="salary-row-detail-grid">
            {detailItems.map(({ key, label, value }) => (
              <div className="salary-row-detail-item" key={key}>
                <div className="salary-row-detail-label">{label}</div>
                <div className="salary-row-detail-value">{value}</div>
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDetailRow(null)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
